import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { useLang } from '../hooks/useLang';
import './BrowseRequests.css';

const COUNTRY_FLAG = { Germany: '🇩🇪', Tunisia: '🇹🇳' };

// ── Trip selector modal (transporter proposing) ──────────────────────────────
function TripSelectorModal({ request, trips, onUseTrip, onCreateTrip, onClose, loading, needsDocs }) {
  const { t } = useLang();
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: '540px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontFamily: 'Syne', fontWeight: '700', fontSize: '18px', margin: 0 }}>Carry this request</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', margin: '16px 0', fontSize: '14px', lineHeight: '1.8' }}>
          <div style={{ fontWeight: '700', marginBottom: '6px' }}>
            {COUNTRY_FLAG[request.origin_country]} {request.origin_city} → {COUNTRY_FLAG[request.destination_country]} {request.destination_city}
          </div>
          <div style={{ color: 'var(--olive)' }}>
            {request.kg_needed > 0 && <span>📦 {request.kg_needed} kg · </span>}
            {request.needs_documents && request.document_count > 0 && (
              <span style={{ color: '#4CAF7D' }}>📄 {request.document_count} × {
                request.document_type === 'official' ? 'Official doc' :
                request.document_type === 'business' ? 'Business doc' :
                request.document_type === 'letter'   ? 'Letter/envelope' :
                request.document_type === 'small_parcel' ? 'Small parcel' : 'Document'
              } · </span>
            )}
            Needed by {format(new Date(request.needed_by_date), 'MMM d, yyyy')}
          </div>
          {request.description && <div style={{ color: 'rgba(245,237,216,0.7)', marginTop: '4px' }}>{request.description}</div>}
          {request.needs_documents && (
            <div style={{ marginTop: '6px', padding: '6px 10px', background: 'rgba(76,175,125,0.08)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '8px', fontSize: '12px', color: '#4CAF7D' }}>
              📄 This request includes documents — only trips that accept documents are shown
            </div>
          )}
          {request.budget_per_kg && (
            <div style={{ color: 'var(--olive)', marginTop: '4px' }}>💶 Sender budget: €{request.budget_per_kg}/kg</div>
          )}
        </div>

        {trips.length > 0 ? (
          <>
            <p style={{ fontSize: '13px', color: 'var(--olive)', marginBottom: '12px' }}>
              You have {trips.length} trip{trips.length > 1 ? 's' : ''} that match this request:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              {trips.map(trip => (
                <div key={trip.id} style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${trip.is_multi_stop ? 'rgba(232,52,26,0.3)' : 'var(--border)'}`, borderRadius: '12px', padding: '14px 16px' }}>
                  {/* Trip header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {trip.is_multi_stop && <span style={{ fontSize: '10px', background: 'rgba(232,52,26,0.15)', border: '1px solid rgba(232,52,26,0.3)', color: 'var(--red)', borderRadius: '100px', padding: '1px 7px', fontWeight: '700' }}>🛑 multi-stop</span>}
                        {trip.origin_city} → {trip.destination_city}
                      </div>
                      <div style={{ color: 'var(--olive)', fontSize: '13px', marginTop: '3px' }}>
                        Departs {format(new Date(trip.departure_date), 'MMM d, yyyy')} · {trip.available_kg} kg left · €{trip.price_per_kg}/kg{trip.accepts_documents && trip.document_price ? ` · 📄 €${trip.document_price}/doc` : ''}
                      </div>
                    </div>
                    <button className="btn btn-primary" style={{ fontSize: '13px', padding: '8px 16px', whiteSpace: 'nowrap' }} onClick={() => onUseTrip(trip)} disabled={loading}>
                      {loading ? 'Sending...' : 'Use this trip →'}
                    </button>
                  </div>
                  {/* Show matched stops for multi-stop trips */}
                  {trip.is_multi_stop && trip._matchedPickupStop && trip._matchedDeliveryStop && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                      <div style={{ background: 'rgba(232,52,26,0.1)', border: '1px solid rgba(232,52,26,0.2)', borderRadius: '8px', padding: '6px 10px', fontSize: '12px' }}>
                        📦 Pickup: <strong>{trip._matchedPickupStop.city}</strong>
                        {trip._matchedPickupStop.stop_date && ` · ${format(new Date(trip._matchedPickupStop.stop_date), 'MMM d')}`}
                        {trip._matchedPickupStop.stop_date_until && ` → ${format(new Date(trip._matchedPickupStop.stop_date_until), 'MMM d')}`}
                        {trip._matchedPickupStop.available_kg != null && <span style={{ color: 'var(--olive)', marginLeft: '4px' }}>({trip._matchedPickupStop.available_kg} kg left)</span>}
                      </div>
                      <div style={{ background: 'rgba(76,175,125,0.1)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '8px', padding: '6px 10px', fontSize: '12px' }}>
                        🏁 Delivery: <strong>{trip._matchedDeliveryStop.city}</strong>
                        {trip._matchedDeliveryStop.stop_date && ` · ${format(new Date(trip._matchedDeliveryStop.stop_date), 'MMM d')}`}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
              <p style={{ fontSize: '13px', color: 'var(--olive)', marginBottom: '10px' }}>Or create a new trip specifically for this request:</p>
              <button className="btn btn-outline" style={{ width: '100%' }} onClick={onCreateTrip}>+ Create a new trip</button>
            </div>
          </>
        ) : (
          <>
            {needsDocs ? (
              <div style={{ background: 'rgba(76,175,125,0.06)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '12px', padding: '14px', marginBottom: '16px', fontSize: '13px' }}>
                <div style={{ fontWeight: '700', color: '#4CAF7D', marginBottom: '6px' }}>📄 This request includes documents</div>
                <div style={{ color: 'var(--olive)', lineHeight: '1.6' }}>
                  None of your active trips accept documents. To carry this request you must create a new trip with document acceptance enabled — it will be pre-filled for you.
                </div>
              </div>
            ) : (
              <p style={{ fontSize: '14px', color: 'var(--olive)', marginBottom: '16px' }}>
                You don't have any active trips matching this route. Create a new trip for this request.
              </p>
            )}
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={onCreateTrip}>
              {needsDocs ? '+ Create a trip with documents enabled →' : '+ Create a trip for this request'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Inline proposals panel (shown when sender expands a request card) ────────
function ProposalsPanel({ request, onAccept, checkoutLoading }) {
  const { t } = useLang();
  const [proposals, setProposals] = useState(null);
  const [loadingProposals, setLoadingProposals] = useState(true);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('bookings')
        .select('*, trips(id, departure_date, price_per_kg, transporter_id, available_kg, profiles!transporter_id(id, full_name, rating, total_reviews, is_verified))')
        .eq('shipment_request_id', request.id)
        .eq('status', 'proposed')
        .order('created_at', { ascending: false });
      setProposals(data || []);
      setLoadingProposals(false);
    }
    load();
  }, [request.id]);

  if (loadingProposals) return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <div className="spinner" style={{ width: '20px', height: '20px', margin: '0 auto' }} />
    </div>
  );

  if (proposals.length === 0) return (
    <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(245,237,216,0.4)', fontSize: '13px' }}>
      ⏳ No proposals yet — transporters will propose once they see your request
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.5)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {proposals.length} proposal{proposals.length > 1 ? 's' : ''} received
      </div>
      {proposals.map(p => {
        const trip = p.trips;
        const transporter = trip?.profiles;
        const stars = '★'.repeat(Math.round(transporter?.rating || 0)) + '☆'.repeat(5 - Math.round(transporter?.rating || 0));
        return (
          <div key={p.id} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(232,197,71,0.25)', borderRadius: '12px', padding: '14px 16px', display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '180px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '15px', flexShrink: 0 }}>
                  {transporter?.full_name?.[0] || '?'}
                </div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>
                    {transporter?.full_name}
                    {transporter?.is_verified && <span className="badge badge-green" style={{ fontSize: '10px', marginLeft: '6px' }}>✓</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--olive)' }}>
                    {stars} {transporter?.total_reviews ? `· ${transporter.total_reviews} reviews` : '· No reviews yet'}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(245,237,216,0.7)', lineHeight: '1.8' }}>
                <div>📅 Departs {trip?.departure_date ? format(new Date(trip.departure_date), 'MMM d, yyyy') : '—'}</div>
                <div>💶 €{trip?.price_per_kg}/kg · Total: <strong style={{ color: 'var(--sand)' }}>€{p.total_price?.toFixed(2)}</strong></div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', minWidth: '130px' }}>
              <button
                className="btn btn-primary"
                style={{ fontSize: '13px', padding: '9px 16px' }}
                onClick={() => onAccept(p)}
                disabled={checkoutLoading === p.id}
              >
                {checkoutLoading === p.id ? '↗ Redirecting...' : '✅ Accept & Pay'}
              </button>
              <Link to={`/transporter/${transporter?.id}`} className="btn btn-ghost" style={{ fontSize: '12px', padding: '6px 12px', textAlign: 'center' }}>
                View Profile
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Sender's own request card ────────────────────────────────────────────────
function SenderRequestCard({ request, onCancel, onAccept, checkoutLoading }) {
  const [expanded, setExpanded] = useState(false);
  const daysLeft = Math.ceil((new Date(request.needed_by_date) - new Date()) / (1000 * 60 * 60 * 24));
  const isUrgent = daysLeft <= 7 && daysLeft >= 0;
  const isExpired = daysLeft < 0;
  const isFilled = request.status === 'filled';
  const proposalCount = request.proposal_count || 0;
  const hasProposals = proposalCount > 0;

  return (
    <div className={`request-card ${isExpired ? 'expired' : ''} ${isFilled ? 'filled' : ''}`}>
      <div className="request-card-header">
        <div className="request-route">
          <span className="request-city">{COUNTRY_FLAG[request.origin_country]} {request.origin_city}</span>
          <span className="request-arrow">→</span>
          <span className="request-city">{COUNTRY_FLAG[request.destination_country]} {request.destination_city}</span>
        </div>
        <div className="request-badges">
          {isFilled ? <span className="badge badge-green">✓ Filled</span>
            : isExpired ? <span className="badge badge-gray">Expired</span>
            : isUrgent ? <span className="badge badge-red">🔥 {daysLeft}d left</span>
            : <span className="badge badge-yellow">{daysLeft} days left</span>}
        </div>
      </div>

      <p className="request-description">{request.description}</p>

      <div className="request-meta">
        <div className="request-meta-item">
          <span className="request-meta-label">Weight</span>
          <span className="request-meta-val">
            {request.kg_needed > 0 && `${request.kg_needed} kg`}
            {request.kg_needed > 0 && request.needs_documents ? ' + ' : ''}
            {request.needs_documents && request.document_count > 0 && (
              <span style={{ color: '#4CAF7D' }}>📄 {request.document_count} doc</span>
            )}
          </span>
        </div>
        <div className="request-meta-item">
          <span className="request-meta-label">Needed by</span>
          <span className="request-meta-val">{format(new Date(request.needed_by_date), 'MMM d, yyyy')}</span>
        </div>
        {request.budget_per_kg && (
          <div className="request-meta-item">
            <span className="request-meta-label">Budget</span>
            <span className="request-meta-val">€{request.budget_per_kg}/kg</span>
          </div>
        )}
        <div className="request-meta-item">
          <span className="request-meta-label">Posted</span>
          <span className="request-meta-val">{format(new Date(request.created_at), 'MMM d')}</span>
        </div>
      </div>

      {request.notes && <p className="request-notes">💬 {request.notes}</p>}

      <div className="request-actions" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        {!isFilled && !isExpired && (
          <button
            className={`btn ${hasProposals ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: '13px', padding: '8px 16px' }}
            onClick={() => setExpanded(e => !e)}
          >
            {hasProposals
              ? `📬 ${proposalCount} proposal${proposalCount > 1 ? 's' : ''} ${expanded ? '▲' : '▼'}`
              : `⏳ Waiting for proposals ${expanded ? '▲' : '▼'}`}
          </button>
        )}
        {isFilled && <span style={{ fontSize: '13px', color: '#2ecc71' }}>✓ This request has been fulfilled</span>}
        {!isFilled && (
          <button className="btn btn-ghost" style={{ fontSize: '13px', padding: '8px 14px', color: 'var(--red)' }} onClick={() => onCancel(request.id)}>
            Cancel request
          </button>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: '14px', borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
          <ProposalsPanel request={request} onAccept={onAccept} checkoutLoading={checkoutLoading} />
        </div>
      )}
    </div>
  );
}

// ── Transporter's view of an open request ───────────────────────────────────
function TransporterRequestCard({ request, onCarry, carrying }) {
  const { t } = useLang();
  const daysLeft = Math.ceil((new Date(request.needed_by_date) - new Date()) / (1000 * 60 * 60 * 24));
  const isUrgent = daysLeft <= 7 && daysLeft >= 0;
  const isExpired = daysLeft < 0;
  const isFilled = request.status === 'filled';
  const alreadyProposed = request.already_proposed;

  return (
    <div className={`request-card ${isExpired ? 'expired' : ''} ${isFilled ? 'filled' : ''}`}>
      <div className="request-card-header">
        <div className="request-route">
          <span className="request-city">{COUNTRY_FLAG[request.origin_country]} {request.origin_city}</span>
          <span className="request-arrow">→</span>
          <span className="request-city">{COUNTRY_FLAG[request.destination_country]} {request.destination_city}</span>
        </div>
        <div className="request-badges">
          {isFilled ? <span className="badge badge-green">✓ Filled</span>
            : isExpired ? <span className="badge badge-gray">Expired</span>
            : isUrgent ? <span className="badge badge-red">🔥 {daysLeft}d left</span>
            : <span className="badge badge-yellow">{daysLeft} days left</span>}
        </div>
      </div>

      <p className="request-description">{request.description}</p>

      <div className="request-meta">
        {request.kg_needed > 0 && (
          <div className="request-meta-item"><span className="request-meta-label">Weight</span><span className="request-meta-val">{request.kg_needed} kg</span></div>
        )}
        {request.needs_documents && request.document_count > 0 && (
          <div className="request-meta-item">
            <span className="request-meta-label">📄 Documents</span>
            <span className="request-meta-val" style={{ color: '#4CAF7D' }}>
              {request.document_count} × {
                request.document_type === 'official'     ? 'Official document' :
                request.document_type === 'business'     ? 'Business document' :
                request.document_type === 'letter'       ? 'Letter / envelope' :
                request.document_type === 'small_parcel' ? 'Small parcel' : 'Document'
              }
            </span>
          </div>
        )}
        <div className="request-meta-item"><span className="request-meta-label">Needed by</span><span className="request-meta-val">{format(new Date(request.needed_by_date), 'MMM d, yyyy')}</span></div>
        {request.budget_per_kg && (
          <div className="request-meta-item"><span className="request-meta-label">Budget</span><span className="request-meta-val">€{request.budget_per_kg}/kg</span></div>
        )}
        <div className="request-meta-item">
          <span className="request-meta-label">Posted by</span>
          <span className="request-meta-val">
            <Link to={`/sender/${request.sender_id}`} style={{ color: 'var(--sand)', textDecoration: 'underline', textDecorationColor: 'rgba(245,237,216,0.3)' }}>
              {request.profiles?.full_name?.split(' ')[0] || 'Sender'}
            </Link>
          </span>
        </div>
      </div>

      {request.notes && <p className="request-notes">💬 {request.notes}</p>}

      <div className="request-actions">
        {!isExpired && !isFilled && !alreadyProposed && (
          <button className="btn btn-primary" style={{ fontSize: '13px', padding: '9px 18px' }} onClick={() => onCarry(request)} disabled={carrying === request.id}>
            {carrying === request.id ? 'Checking trips...' : '✈️ I can carry this'}
          </button>
        )}
        {!isExpired && !isFilled && alreadyProposed && (
          <span className="badge badge-yellow" style={{ fontSize: '12px', padding: '6px 12px' }}>
            📬 Proposal sent — awaiting sender
          </span>
        )}
        <span className="request-posted-at">{t('requests.posted')} {format(new Date(request.created_at), 'MMM d')}</span>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function BrowseRequests() {
  const { user, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const justPosted = searchParams.get('posted') === '1';

  const [openRequests, setOpenRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState('all');
  const [modal, setModal] = useState(null);
  const [carrying, setCarrying] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const isSender = profile?.role === 'sender' || profile?.role === 'both';
  const isTransporter = profile?.role === 'transporter' || profile?.role === 'both';
  const senderOnly = profile?.role === 'sender';

  useEffect(() => { if (profile) fetchRequests(); }, [user, profile?.role]);

  async function fetchRequests() {
    setLoading(true);

    const fetches = [];

    // Transporters see open requests + mark ones they already proposed on
    if (isTransporter && user) {
      fetches.push((async () => {
        const [{ data: allOpen }, { data: myTrips }] = await Promise.all([
          supabase.from('shipment_requests').select('*, profiles!sender_id(full_name)').eq('status', 'open').order('created_at', { ascending: false }),
          supabase.from('trips').select('id').eq('transporter_id', user.id),
        ]);
        const myTripIds = (myTrips || []).map(t => t.id);
        let proposedRequestIds = new Set();
        if (myTripIds.length > 0) {
          const { data: myProposals } = await supabase
            .from('bookings')
            .select('shipment_request_id')
            .in('trip_id', myTripIds)
            .eq('status', 'proposed')
            .not('shipment_request_id', 'is', null);
          (myProposals || []).forEach(p => proposedRequestIds.add(p.shipment_request_id));
        }
        setOpenRequests((allOpen || []).map(r => ({ ...r, already_proposed: proposedRequestIds.has(r.id) })));
      })());
    }

    // Senders see their own requests + proposal counts
    if (isSender && user) {
      fetches.push((async () => {
        const { data: mine } = await supabase
          .from('shipment_requests')
          .select('*')
          .eq('sender_id', user.id)
          .in('status', ['open', 'filled'])
          .order('created_at', { ascending: false });

        const myList = mine || [];
        if (myList.length > 0) {
          const { data: counts } = await supabase
            .from('bookings')
            .select('shipment_request_id')
            .in('shipment_request_id', myList.map(r => r.id))
            .eq('status', 'proposed');
          const countMap = {};
          (counts || []).forEach(p => { countMap[p.shipment_request_id] = (countMap[p.shipment_request_id] || 0) + 1; });
          setMyRequests(myList.map(r => ({ ...r, proposal_count: countMap[r.id] || 0 })));
        } else {
          setMyRequests([]);
        }
      })());
    }

    await Promise.all(fetches);
    setLoading(false);
  }

  async function cancelRequest(id) {
    if (!window.confirm(t('dashboard.confirmCancelRequestBrowse'))) return;
    const { error } = await supabase.from('shipment_requests').update({ status: 'cancelled' }).eq('id', id);
    if (error) { alert(t('dashboard.alertCancelError')); return; }

    const { data: proposals } = await supabase
      .from('bookings').select('id, trip_id, marked_full, trips(transporter_id)')
      .eq('shipment_request_id', id).eq('status', 'proposed');

    for (const p of proposals || []) {
      await supabase.from('bookings').update({ status: 'rejected' }).eq('id', p.id);
      if (p.marked_full && p.trip_id) await supabase.from('trips').update({ status: 'cancelled' }).eq('id', p.trip_id);
      if (p.trips?.transporter_id) {
        await supabase.functions.invoke('send-notification', {
          body: { notification_context: 'proposal_rejected', notify_user_id: p.trips.transporter_id, record: { id: p.id, trip_id: p.trip_id, marked_full: p.marked_full } },
        });
      }
    }
    setMyRequests(r => r.filter(x => x.id !== id));
    setOpenRequests(r => r.filter(x => x.id !== id));
  }

  async function handleAcceptProposal(booking) {
    setCheckoutLoading(booking.id);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { booking_id: booking.id, sender_id: user.id },
      });
      if (error || !data?.url) throw new Error(error?.message || 'No URL');
      window.location.href = data.url;
    } catch {
      alert(t('dashboard.alertPaymentStartError'));
      setCheckoutLoading(null);
    }
  }

  async function handleCarry(request) {
    if (!user) { navigate('/login'); return; }
    setCarrying(request.id);

    // ── Simple trips: exact city match ────────────────────────
    const { data: simpleTrips } = await supabase
      .from('trips')
      .select('id, departure_date, available_kg, price_per_kg, total_kg, payment_mode, is_multi_stop, origin_city, destination_city, accepts_documents, document_price')
      .eq('transporter_id', user.id).eq('status', 'active').eq('is_multi_stop', false)
      .eq('origin_country', request.origin_country).eq('destination_country', request.destination_country)
      .eq('origin_city', request.origin_city).eq('destination_city', request.destination_city)
      .lte('departure_date', request.needed_by_date)
      .gte('available_kg', request.kg_needed || 0)
      .order('departure_date', { ascending: true });

    // ── Multi-stop trips: check if request cities match any stops ──
    const { data: multiTrips } = await supabase
      .from('trips')
      .select('id, departure_date, available_kg, price_per_kg, total_kg, payment_mode, is_multi_stop, origin_city, destination_city, accepts_documents, document_price, trip_stops(id, city, country, stop_type, stop_date, stop_date_until, available_kg, address)')
      .eq('transporter_id', user.id).eq('status', 'active').eq('is_multi_stop', true)
      .gte('available_kg', request.kg_needed || 0)
      .order('departure_date', { ascending: true });

    // Filter multi-stop trips: need a matching pickup stop + delivery stop with capacity
    const matchingMultiTrips = (multiTrips || []).filter(trip => {
      const stops = trip.trip_stops || [];

      // Match pickup stop: city + country
      const pickupStops = stops.filter(s =>
        (s.stop_type === 'pickup' || s.stop_type === 'both') &&
        s.city === request.origin_city &&
        s.country === request.origin_country
      );
      // Match delivery stop: city + country
      const deliveryStops = stops.filter(s =>
        (s.stop_type === 'delivery' || s.stop_type === 'both') &&
        s.city === request.destination_city &&
        s.country === request.destination_country
      );
      if (!pickupStops.length || !deliveryStops.length) return false;

      // Date check: the pickup window (stop_date → stop_date_until) must include
      // or precede the needed_by_date. Use stop_date_until if set, else stop_date.
      const pickupOk = pickupStops.some(s => {
        if (!request.needed_by_date) return true;
        const windowEnd = s.stop_date_until || s.stop_date;
        return windowEnd <= request.needed_by_date;
      });
      if (!pickupOk) return false;

      // Capacity check: prefer per-stop available_kg if explicitly set and > 0.
      // If stop available_kg is null (not set) OR 0 (depleted/unset), fall back
      // to trip-level available_kg which is the authoritative remaining capacity.
      const capacityOk = pickupStops.some(s => {
        if (s.available_kg == null || s.available_kg === 0) {
          // Fall back to trip-level capacity
          return trip.available_kg >= request.kg_needed;
        }
        return s.available_kg >= request.kg_needed;
      });
      return capacityOk;
    }).map(trip => ({
      ...trip,
      _matchedPickupStop:   (trip.trip_stops || []).find(s =>
        (s.stop_type === 'pickup' || s.stop_type === 'both') &&
        s.city === request.origin_city &&
        s.country === request.origin_country
      ),
      _matchedDeliveryStop: (trip.trip_stops || []).find(s =>
        (s.stop_type === 'delivery' || s.stop_type === 'both') &&
        s.city === request.destination_city &&
        s.country === request.destination_country
      ),
    }));

    // Filter: if request needs documents, only show trips that accept documents
    const needsDocs = request.needs_documents && (request.document_count || 0) > 0;
    const allMatching = [...(simpleTrips || []), ...matchingMultiTrips].filter(trip => {
      if (needsDocs && !trip.accepts_documents) return false;
      return true;
    });
    setCarrying(null);
    setModal({ request, matchingTrips: allMatching, needsDocs });
  }

  async function handleUseTrip(trip) {
    const { request } = modal;
    setBookingLoading(true);
    const kgSubtotal  = (trip.price_per_kg || 0) * (request.kg_needed || 0);
    const docSubtotal = (request.needs_documents && request.document_count > 0)
      ? (trip.document_price || 0) * request.document_count
      : 0;
    const totalPrice  = kgSubtotal + docSubtotal;
    const platformFee = totalPrice * 0.10;

    const { data: booking, error } = await supabase.from('bookings').insert({
      trip_id: trip.id, sender_id: request.sender_id,
      kg_booked: request.kg_needed || 0,
      total_price: totalPrice, platform_fee: platformFee, status: 'proposed',
      payment_status: 'unpaid', sender_payment_mode: trip.payment_mode === 'cash' ? 'cash' : 'platform',
      description: request.description, shipment_request_id: request.id, marked_full: false,
      recipient_name: request.recipient_name || null,
      recipient_phone: request.recipient_phone || null,
      pickup_stop_id:   trip._matchedPickupStop?.id   || null,
      delivery_stop_id: trip._matchedDeliveryStop?.id || null,
      booking_type: (!request.kg_needed || request.kg_needed === 0) && request.needs_documents ? 'document' : 'kg',
      document_type:  request.needs_documents ? request.document_type : null,
      document_count: request.needs_documents ? request.document_count : null,
    }).select('id').single();

    if (error) { setBookingLoading(false); alert(t('dashboard.alertProposalError')); return; }

    await supabase.functions.invoke('send-notification', {
      body: { notification_context: 'proposal_received', notify_user_id: request.sender_id,
        record: { id: booking.id, trip_id: trip.id, sender_id: request.sender_id, kg_booked: request.kg_needed || 0, total_price: totalPrice, platform_fee: platformFee, booking_type: (!request.kg_needed || request.kg_needed === 0) && request.needs_documents ? 'document' : 'kg', document_type: request.document_type, document_count: request.document_count } },
    });

    setBookingLoading(false);
    setModal(null);
    alert(t('requests.proposalSentAlert'));
  }

  function handleCreateTrip() {
    const { request } = modal;
    const params = new URLSearchParams({
      from_request: request.id, origin_city: request.origin_city, origin_country: request.origin_country,
      destination_city: request.destination_city, destination_country: request.destination_country,
      needed_by: request.needed_by_date, kg: request.kg_needed || 0,
      ...(request.needs_documents ? { requires_documents: '1' } : {}),
    });
    navigate(`/post-trip?${params.toString()}`);
  }

  const filteredOpen = openRequests.filter(r => {
    if (direction === 'de_to_tn' && r.origin_country !== 'Germany') return false;
    if (direction === 'tn_to_de' && r.origin_country !== 'Tunisia') return false;
    return true;
  });

  return (
    <div className="page browse-requests-page">
      <div className="container">
        {modal && (
          <TripSelectorModal request={modal.request} trips={modal.matchingTrips} needsDocs={modal.needsDocs} onUseTrip={handleUseTrip} onCreateTrip={handleCreateTrip} onClose={() => setModal(null)} loading={bookingLoading} />
        )}

        {justPosted && (
          <div className="alert alert-success" style={{ marginBottom: '24px' }}>
            ✓ Your shipment request is live. Transporters can now find and carry your goods.
          </div>
        )}

        {/* ── SENDER ONLY VIEW ───────────────────────────────────────────── */}
        {senderOnly ? (
          <>
            <div className="requests-header">
              <div>
                <h1>{t('requests.title')}</h1>
                <p>Post what you need shipped — transporters will pick it up.</p>
              </div>
              <Link to="/post-request" className="btn btn-primary">+ Post a Request</Link>
            </div>

            {loading ? (
              <div className="requests-grid">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="request-card-skeleton skeleton-card" style={{ height: '200px' }}>
                    <div className="skeleton skeleton-title" /><div className="skeleton skeleton-line long" style={{ marginBottom: '8px' }} /><div className="skeleton skeleton-line medium" />
                  </div>
                ))}
              </div>
            ) : myRequests.length === 0 ? (
              <div className="requests-empty">
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
                <h3>{t('requests.noMine')}</h3>
                <p>Post a request and transporters will propose to carry your goods.</p>
                <Link to="/post-request" className="btn btn-primary" style={{ marginTop: '16px' }}>Post a Request</Link>
              </div>
            ) : (
              <div className="requests-grid">
                {myRequests.map(r => (
                  <SenderRequestCard key={r.id} request={r} onCancel={cancelRequest} onAccept={handleAcceptProposal} checkoutLoading={checkoutLoading} />
                ))}
              </div>
            )}
          </>
        ) : (
          /* ── TRANSPORTER / BOTH VIEW ────────────────────────────────────── */
          <>
            <div className="requests-header">
              <div>
                <h1>{t('requests.title')}</h1>
                <p>Senders looking for a transporter — carry their goods on your next trip.</p>
              </div>
              {isSender && <Link to="/post-request" className="btn btn-primary">+ Post a Request</Link>}
            </div>

            <div className="requests-filters">
              {[
                { id: 'all', label: '🌍 All directions' },
                { id: 'de_to_tn', label: '🇩🇪 → 🇹🇳 Germany to Tunisia' },
                { id: 'tn_to_de', label: '🇹🇳 → 🇩🇪 Tunisia to Germany' },
              ].map(d => (
                <button key={d.id} className={`dir-btn ${direction === d.id ? 'active' : ''}`} onClick={() => setDirection(d.id)}>{d.label}</button>
              ))}
            </div>

            {loading ? (
              <div className="requests-grid">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="request-card-skeleton skeleton-card" style={{ height: '200px' }}>
                    <div className="skeleton skeleton-title" /><div className="skeleton skeleton-line long" style={{ marginBottom: '8px' }} /><div className="skeleton skeleton-line medium" />
                  </div>
                ))}
              </div>
            ) : filteredOpen.length === 0 ? (
              <div className="requests-empty">
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
                <h3>{t('requests.noOpen')}</h3>
                <p>Check back soon — senders post new requests regularly.</p>
              </div>
            ) : (
              <div className="requests-grid">
                {filteredOpen.map(r => (
                  <TransporterRequestCard key={r.id} request={r} onCarry={handleCarry} carrying={carrying} />
                ))}
              </div>
            )}

            {/* 'Both' role: show their own requests below the open ones */}
            {isSender && isTransporter && myRequests.length > 0 && (
              <div style={{ marginTop: '48px' }}>
                <h2 style={{ fontFamily: 'Syne', fontWeight: '800', fontSize: '20px', marginBottom: '20px', borderTop: '1px solid var(--border)', paddingTop: '32px' }}>
                  My Requests
                </h2>
                <div className="requests-grid">
                  {myRequests.map(r => (
                    <SenderRequestCard key={r.id} request={r} onCancel={cancelRequest} onAccept={handleAcceptProposal} checkoutLoading={checkoutLoading} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}